import {useCallback, useEffect, useRef, useState} from 'react';

const BASE = process.env.REACT_APP_AWP_API_URL || 'https://awp.minsky.ai';

function authHeaders() {
    const token = localStorage.getItem('access_token');
    return token ? {'Authorization': `Bearer ${token}`} : {};
}

function viewerQuery() {
    const userId = localStorage.getItem('user_id');
    return userId ? `&userId=${encodeURIComponent(userId)}` : '';
}

/**
 * Lazy-fetch entity intelligence summary when the Summary tab is active.
 *
 * @param {string|null|undefined} entityId
 * @param {{ enabled?: boolean }} [options]
 */
export function useEntityIntelligenceSummary(entityId, {enabled = false} = {}) {
    const [summary, setSummary] = useState('');
    const [cached, setCached] = useState(false);
    const [stale, setStale] = useState(false);
    const [manuallyEdited, setManuallyEdited] = useState(false);
    const [editedAt, setEditedAt] = useState(null);
    const [editedBy, setEditedBy] = useState(null);
    const [generatedAt, setGeneratedAt] = useState(null);
    const [sourceCounts, setSourceCounts] = useState(null);
    const [loading, setLoading] = useState(false);
    const [regenerating, setRegenerating] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const fetchGeneration = useRef(0);
    const regeneratingRef = useRef(false);
    regeneratingRef.current = regenerating;

    const applyPayload = useCallback((payload) => {
        setSummary(typeof payload?.summary === 'string' ? payload.summary : '');
        setCached(Boolean(payload?.cached));
        setStale(Boolean(payload?.stale));
        setManuallyEdited(Boolean(payload?.manuallyEdited));
        setEditedAt(payload?.editedAt ?? null);
        setEditedBy(payload?.editedBy ?? null);
        setGeneratedAt(payload?.generatedAt ?? null);
        setSourceCounts(payload?.sourceCounts ?? null);
    }, []);

    const fetchSummary = useCallback(async (forceRefresh = false, expertContext = null) => {
        if (!entityId) {
            setSummary('');
            setCached(false);
            setStale(false);
            setManuallyEdited(false);
            setEditedAt(null);
            setEditedBy(null);
            setGeneratedAt(null);
            setSourceCounts(null);
            setLoading(false);
            setError(null);
            return;
        }

        const generation = ++fetchGeneration.current;
        setError(null);
        if (forceRefresh) {
            setRegenerating(true);
        } else {
            setLoading(true);
        }

        try {
            const url = forceRefresh
                ? `${BASE}/api/entities/${encodeURIComponent(entityId)}/intelligence-summary${viewerQuery()}`
                : `${BASE}/api/entities/${encodeURIComponent(entityId)}/intelligence-summary?forceRefresh=${forceRefresh ? 'true' : 'false'}${viewerQuery()}`;

            const response = await fetch(url, {
                method: forceRefresh ? 'POST' : 'GET',
                headers: {
                    ...authHeaders(),
                    ...(forceRefresh ? {'Content-Type': 'application/json'} : {}),
                },
                ...(forceRefresh && expertContext != null
                    ? {body: JSON.stringify({expertContext})}
                    : {}),
            });

            if (!response.ok) {
                throw new Error(`Failed to load summary (${response.status})`);
            }

            const payload = await response.json();
            if (generation !== fetchGeneration.current) {
                return;
            }
            applyPayload(payload);
        } catch (err) {
            if (generation === fetchGeneration.current) {
                setError(err);
            }
        } finally {
            if (generation === fetchGeneration.current) {
                setLoading(false);
                setRegenerating(false);
            }
        }
    }, [applyPayload, entityId]);

    useEffect(() => {
        if (!enabled || !entityId) {
            return undefined;
        }
        // A property/note mutation can start regenerate while Summary is hidden.
        // Do not cancel that POST with a cached GET when the tab becomes active.
        if (regeneratingRef.current) {
            return undefined;
        }
        fetchSummary(false);
        return () => {
            fetchGeneration.current += 1;
        };
    }, [enabled, entityId, fetchSummary]);

    const regenerate = useCallback(
        (expertContext = null) => fetchSummary(true, expertContext),
        [fetchSummary],
    );

    const regenerateAfterSourceChange = useCallback(() => {
        if (manuallyEdited) {
            return fetchSummary(true, summary || '');
        }
        return fetchSummary(true);
    }, [fetchSummary, manuallyEdited, summary]);

    const saveSummary = useCallback(async (summaryText) => {
        if (!entityId) return;
        const generation = ++fetchGeneration.current;
        setSaving(true);
        setError(null);
        try {
            const response = await fetch(
                `${BASE}/api/entities/${encodeURIComponent(entityId)}/intelligence-summary`,
                {
                    method: 'PATCH',
                    headers: {
                        ...authHeaders(),
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({summary: summaryText}),
                },
            );
            if (!response.ok) {
                throw new Error(`Failed to save summary (${response.status})`);
            }
            const payload = await response.json();
            if (generation === fetchGeneration.current) {
                applyPayload(payload);
            }
        } catch (err) {
            if (generation === fetchGeneration.current) {
                setError(err);
            }
            throw err;
        } finally {
            if (generation === fetchGeneration.current) {
                setSaving(false);
            }
        }
    }, [applyPayload, entityId]);

    return {
        summary,
        cached,
        stale,
        manuallyEdited,
        editedAt,
        editedBy,
        generatedAt,
        sourceCounts,
        loading,
        regenerating,
        saving,
        error,
        regenerate,
        regenerateAfterSourceChange,
        saveSummary,
        refetch: () => fetchSummary(false),
    };
}
